import { cn } from "@nt/ui/cn";
import { Field, FieldError, FieldHint, FieldLabel } from "@nt/ui/field";
import { buttonVariants, inputVariants } from "@nt/ui/recipes";
import { useId, useState, type FormEvent, type ReactNode } from "react";

import { ChoiceCards, type ChoiceOption } from "@/components/choice-cards";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@nt/ui/sheet";
import { customLinkUrl, slugifyTag } from "@nt/partner-links";

import { ApiError, messageOf } from "@/lib/api";
import {
  LINK_NAME_MAX,
  LINK_NOTE_MAX,
  resolveDestination,
  validateLinkName,
  validateLinkNote,
  validateTag,
} from "@/lib/links";
import type { LinksData } from "@/lib/types";

/** What `POST /api/partner/links` takes. */
export interface CreateLinkInput {
  name: string;
  note: string | null;
  destination: string;
  tag: string | null;
}

/** The ChoiceCards value that reveals the address input. */
const OTHER_PAGE = "__other";

type Pages = NonNullable<LinksData["pages"]>;

interface FormValues {
  name: string;
  note: string;
  /** A page path, or `OTHER_PAGE`. */
  destination: string;
  /** The typed path or address when `destination` is `OTHER_PAGE`. */
  other: string;
  tag: string;
}

export interface CreateLinkFormProps {
  siteOrigin: string;
  code: string;
  pages: Pages;
  pending: boolean;
  /** The last failed create, shown inline (a taken tag under the tag field). */
  error: unknown;
  onSubmit: (input: CreateLinkInput) => void;
  onCancel: () => void;
  /** Starting values (tests, or a form reopened with what was typed). */
  defaultValues?: Partial<FormValues>;
}

/** One numbered part of the form, like PartnerStack's drawer. */
function Step({
  n,
  title,
  description,
  children,
}: {
  n: number;
  title: string;
  description: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      className="grid gap-4 border-t border-border py-5 first:border-t-0 first:pt-1"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
        >
          {n}
        </span>
        <div className="grid gap-0.5">
          <h3 id={id} className="text-sm font-semibold text-foreground">
            {title}
          </h3>
          <p className="text-compact text-muted-foreground">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function initialValues(
  pages: Pages,
  defaults: Partial<FormValues> | undefined,
): FormValues {
  return {
    name: "",
    note: "",
    other: "",
    tag: "",
    ...defaults,
    destination:
      pages.length === 0
        ? OTHER_PAGE
        : (defaults?.destination ?? pages[0]?.path ?? OTHER_PAGE),
  };
}

/** The checked form: every field's problem (or null) and the request when there is none. */
function check(values: FormValues) {
  const destination =
    values.destination === OTHER_PAGE
      ? resolveDestination(values.other)
      : ({ ok: true, path: values.destination } as const);
  const errors = {
    name: validateLinkName(values.name),
    note: validateLinkNote(values.note),
    destination: destination.ok ? null : destination.message,
    tag: validateTag(values.tag),
  };
  const input: CreateLinkInput | null =
    destination.ok && !errors.name && !errors.note && !errors.tag
      ? {
          name: values.name.trim(),
          note: values.note.trim() || null,
          destination: destination.path,
          tag: values.tag || null,
        }
      : null;
  return { errors, input, path: destination.ok ? destination.path : null };
}

const FIELD_ORDER = ["name", "note", "destination", "tag"] as const;
const FIELD_ID: Record<(typeof FIELD_ORDER)[number], string> = {
  name: "link-name",
  note: "link-note",
  destination: "link-other",
  tag: "link-tag",
};

function serverError(error: unknown): {
  tag: string | null;
  form: string | null;
} {
  if (!error) return { tag: null, form: null };
  if (error instanceof ApiError && error.code.toUpperCase() === "TAG_TAKEN") {
    return { tag: error.message, form: null };
  }
  return { tag: null, form: messageOf(error) };
}

/**
 * The create-link form: 1 name and note (internal only), 2 destination,
 * 3 tag with the live preview, and a sticky footer. Checks on submit; the
 * address and tag are checked as they are typed.
 */
export function CreateLinkForm({
  siteOrigin,
  code,
  pages,
  pending,
  error,
  onSubmit,
  onCancel,
  defaultValues,
}: CreateLinkFormProps) {
  const [values, setValues] = useState(() =>
    initialValues(pages, defaultValues),
  );
  const [submitted, setSubmitted] = useState(false);
  // The tag sent last: a "tag taken" answer stops showing once it is edited.
  const [sentTag, setSentTag] = useState(defaultValues?.tag ?? "");
  const set = (patch: Partial<FormValues>) =>
    setValues((v) => ({ ...v, ...patch }));
  const { errors, input, path } = check(values);
  const server = serverError(error);
  const suggested = slugifyTag(values.name);
  const tagError = errors.tag ?? (values.tag === sentTag ? server.tag : null);
  const otherError =
    submitted || values.other.trim() ? errors.destination : null;
  const nameError = submitted ? errors.name : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setSentTag(values.tag);
    if (input) {
      onSubmit(input);
      return;
    }
    const first = FIELD_ORDER.find((k) => errors[k]);
    if (first && typeof document !== "undefined") {
      document.getElementById(FIELD_ID[first])?.focus();
    }
  };

  const options: ChoiceOption<string>[] = [
    ...pages.map((p) => ({
      value: p.path,
      label: p.label,
      description: p.path,
    })),
    {
      value: OTHER_PAGE,
      label: "Another page",
      description: "Any page on nonresident.tax",
    },
  ];

  return (
    <form noValidate onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-4 pb-4">
        <Step
          n={1}
          title="Name and note"
          description="Internal only: customers never see these."
        >
          <Field>
            <FieldLabel htmlFor="link-name">Name</FieldLabel>
            <input
              id="link-name"
              autoComplete="off"
              data-1p-ignore=""
              value={values.name}
              aria-describedby="link-name-hint"
              onChange={(e) => set({ name: e.target.value })}
              placeholder="YouTube October video"
              aria-invalid={nameError ? true : undefined}
              className={inputVariants()}
            />
            {nameError ? (
              <FieldError id="link-name-hint" role="alert">
                {nameError}
              </FieldError>
            ) : (
              <FieldHint id="link-name-hint">
                Where you share it, so you can tell links apart. Up to{" "}
                {LINK_NAME_MAX} characters.
              </FieldHint>
            )}
          </Field>
          <Field>
            <FieldLabel htmlFor="link-note">Note (optional)</FieldLabel>
            <textarea
              id="link-note"
              rows={3}
              value={values.note}
              onChange={(e) => set({ note: e.target.value })}
              aria-invalid={errors.note ? true : undefined}
              aria-describedby="link-note-count"
              className={inputVariants({ kind: "textarea" })}
            />
            <div className="flex items-start justify-between gap-3">
              {errors.note ? (
                <FieldError role="alert">{errors.note}</FieldError>
              ) : (
                <FieldHint>Internal only. A reminder for you.</FieldHint>
              )}
              <FieldHint
                id="link-note-count"
                className={cn(
                  "shrink-0 tabular-nums",
                  errors.note && "text-danger",
                )}
              >
                {values.note.length}/{LINK_NOTE_MAX}
              </FieldHint>
            </div>
          </Field>
        </Step>

        <Step
          n={2}
          title="Destination"
          description="The nonresident.tax page people land on."
        >
          {pages.length > 0 ? (
            <ChoiceCards
              name="link-destination"
              aria-label="Destination page"
              columns={2}
              value={values.destination}
              onChange={(destination) => set({ destination })}
              options={options}
            />
          ) : null}
          {values.destination === OTHER_PAGE ? (
            <Field>
              <FieldLabel htmlFor="link-other">Page address</FieldLabel>
              <input
                id="link-other"
                autoComplete="off"
                data-1p-ignore=""
                value={values.other}
                onChange={(e) => set({ other: e.target.value })}
                placeholder="/blog/wyoming-llc/ or https://nonresident.tax/…"
                aria-invalid={otherError ? true : undefined}
                className={inputVariants({ mono: true })}
              />
              {otherError ? (
                <FieldError role="alert">{otherError}</FieldError>
              ) : (
                <FieldHint>
                  A path such as /pricing/ or a full nonresident.tax address.
                </FieldHint>
              )}
            </Field>
          ) : null}
        </Step>

        <Step
          n={3}
          title="Tag"
          description="Optional. Shows which link a click came from."
        >
          <Field>
            <FieldLabel htmlFor="link-tag">Tag (optional)</FieldLabel>
            <input
              id="link-tag"
              autoComplete="off"
              data-1p-ignore=""
              value={values.tag}
              maxLength={40}
              onChange={(e) => set({ tag: e.target.value.toLowerCase() })}
              placeholder={suggested}
              aria-invalid={tagError ? true : undefined}
              className={inputVariants({ mono: true })}
            />
            {tagError ? (
              <FieldError role="alert">{tagError}</FieldError>
            ) : (
              <FieldHint>
                Lowercase letters, numbers and dashes. Leave it empty and we use{" "}
                <span className="font-mono">{suggested}</span>.
              </FieldHint>
            )}
          </Field>
          <div className="grid gap-1 rounded-control bg-muted px-3 py-2.5">
            <span className="text-compact text-muted-foreground">
              Preview. Copy the final link from the list once it is created.
            </span>
            <span className="font-mono text-sm break-all text-foreground">
              {path
                ? customLinkUrl(siteOrigin, path, code, values.tag || suggested)
                : "Pick a page or fix the address to see the link."}
            </span>
          </div>
        </Step>
      </div>

      <SheetFooter className="mt-0 border-t border-border bg-card p-5">
        {server.form ? (
          <p
            role="alert"
            className="rounded-control bg-danger-tint px-3 py-2 text-sm font-medium text-danger"
          >
            {server.form}
          </p>
        ) : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className={buttonVariants({ variant: "secondary" })}
          >
            Cancel
          </button>
          <button type="submit" disabled={pending} className={buttonVariants()}>
            {pending ? "Creating…" : "Create link"}
          </button>
        </div>
      </SheetFooter>
    </form>
  );
}

/**
 * The right-side drawer (full width on a phone, 480px from `sm`). Closing it
 * unmounts the form, so it opens empty next time. `finalFocus` decides where
 * focus goes when it closes (the new link's copy button after a create).
 */
export function CreateLinkSheet({
  open,
  onOpenChange,
  finalFocus,
  ...form
}: Omit<CreateLinkFormProps, "onCancel"> & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  finalFocus: () => HTMLElement | boolean;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        finalFocus={finalFocus}
        className="gap-0 data-[side=right]:w-full sm:data-[side=right]:max-w-[480px]"
      >
        <SheetHeader className="border-b border-border p-5 pr-12">
          <SheetTitle>Create a custom link</SheetTitle>
          <SheetDescription>
            One link per place you share, so you can see which one brings
            sign-ups.
          </SheetDescription>
        </SheetHeader>
        <CreateLinkForm {...form} onCancel={() => onOpenChange(false)} />
      </SheetContent>
    </Sheet>
  );
}
